import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  isOnboardingResponseForProject,
  isProgressionDataLoading,
  progressionQueryOptions
} from '../../utils/progressionQueryUtils';
import { getInventoryOnboardingMap } from '../../services/api/implementation/impl';
import { ProgressionTimelineBody } from './ProgressionTimelineSteps';

const STEP_ROUTES = {
  create_inventory: '/builder/inventory/stock',
  add_vendors: '/builder/inventory/vendors'
};

const STEP_ACTION_LABELS = {
  create_inventory: 'Set up inventory',
  add_vendors: 'Add vendors'
};

const mapStepStatus = (step, currentStepId) => {
  if (step.status === 'completed') return 'completed';
  if (
    step.id === currentStepId ||
    step.status === 'current' ||
    step.status === 'delayed'
  ) {
    return 'active';
  }
  return 'locked';
};

const buildStepDescription = (step) => {
  let description = step.description || null;

  if (step.vendorCount != null && step.vendorMinimum != null) {
    const vendorProgress = `${step.vendorCount}/${step.vendorMinimum} vendors added`;
    description = description ? `${description} (${vendorProgress})` : vendorProgress;
  }

  return description;
};

const mapApiStepsToStages = (steps = [], currentStepId) => {
  const stepTitleById = Object.fromEntries(steps.map((step) => [step.id, step.title]));

  return steps.map((step) => {
    const uiStatus = mapStepStatus(step, currentStepId);
    const actionLabel = STEP_ACTION_LABELS[step.id];
    const needsLabel =
      step.needsLabel ||
      (uiStatus === 'locked' && step.dependsOn?.length
        ? `Needs: ${step.dependsOn.map((id) => stepTitleById[id]).filter(Boolean).join(', ')}`
        : null);

    return {
      id: step.id,
      title: step.title,
      description: buildStepDescription(step),
      needsLabel,
      status: uiStatus,
      owner: step.owner?.name?.trim() || null,
      pendingDays: step.pendingDays,
      tag: step.status === 'delayed' ? 'Delayed' : step.tag,
      taskId: step.taskId,
      route: STEP_ROUTES[step.id] || null,
      actions: uiStatus === 'active' && actionLabel ? [actionLabel] : []
    };
  });
};

const InventoryProgressionView = ({ projectID = null }) => {
  const navigate = useNavigate();
  const isFilterReady = projectID !== null && projectID !== undefined;
  const hasProjectSelected = isFilterReady && String(projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(projectID) : null;

  const {
    data: onboardingResponse,
    isPending,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['inventoryOnboardingMap', resolvedProjectId],
    queryFn: () => getInventoryOnboardingMap(resolvedProjectId),
    enabled: hasProjectSelected,
    ...progressionQueryOptions
  });

  const isInitialLoad = isProgressionDataLoading({
    isPending,
    isFetching,
    response: onboardingResponse,
    projectId: resolvedProjectId
  });

  const stages = useMemo(() => {
    if (!isOnboardingResponseForProject(onboardingResponse, resolvedProjectId)) {
      return [];
    }

    return mapApiStepsToStages(
      onboardingResponse?.data?.steps,
      onboardingResponse?.data?.currentStepId
    );
  }, [onboardingResponse, resolvedProjectId]);

  const progressPercent = isOnboardingResponseForProject(onboardingResponse, resolvedProjectId)
    ? onboardingResponse?.data?.progress?.percent ?? 0
    : 0;

  const handleStageAction = (stage) => {
    if (!stage.route) return;

    const options =
      stage.id === 'create_inventory' && resolvedProjectId
        ? { state: { projectID: resolvedProjectId } }
        : undefined;

    navigate(stage.route, options);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-[#1E4A53]">Inventory Mission Progression</h3>
          {hasProjectSelected && (
            <p className="mt-0.5 text-xs text-gray-500">Project-specific inventory onboarding</p>
          )}
          {hasProjectSelected && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate('/builder/inventory/pipeline', {
                    state: { projectID: resolvedProjectId }
                  })
                }
                className="rounded-lg border border-[#2A565E] px-3 py-1.5 text-xs font-semibold text-[#2A565E]">
                Pipeline
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate('/builder/inventory/site-receipts', {
                    state: { projectID: resolvedProjectId }
                  })
                }
                className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white">
                Site receipts
              </button>
            </div>
          )}
        </div>
        {!isInitialLoad && !isError && hasProjectSelected && (
          <div className="shrink-0 text-right">
            <p className="text-2xl font-bold leading-none text-[#0f2a45]">{progressPercent}%</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Complete
            </p>
          </div>
        )}
      </div>

      {!hasProjectSelected && (
        <div className="py-12 text-center text-sm text-gray-500">
          Select a project to view inventory onboarding progress.
        </div>
      )}

      {hasProjectSelected && isInitialLoad && (
        <div className="py-12 text-center text-sm text-gray-500">
          Loading inventory progression...
        </div>
      )}

      {hasProjectSelected && isError && (
        <div className="py-12 text-center text-sm text-rose-600">
          Unable to load inventory progression. Please try again.
        </div>
      )}

      {hasProjectSelected && !isInitialLoad && !isError && stages.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-500">
          No inventory onboarding steps available for this project yet.
        </div>
      )}

      {hasProjectSelected && !isInitialLoad && !isError && stages.length > 0 && (
        <ProgressionTimelineBody stages={stages} onAction={handleStageAction} />
      )}
    </div>
  );
};

export default InventoryProgressionView;
